import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import MessagePreviewDialog from '../shared/MessagePreviewDialog';
import SmsSegmentCounter from '../shared/SmsSegmentCounter';
import { calculateSmsSegments } from '../../utils/smsSegments';
import { formatCost } from '../service_reminders/reminderUtils';

// "Aikataulumuistutukset" sub-tab of the Ominaisuudet (Features) settings tab.
// Available to every org (no entitlement flag, like sähköiset lähetteet): holds
// the support off-switch, the defaults that prefill the schedule dialogs, and
// the org-level default message texts. Config lives in its own endpoint
// (/api/scheduling/reminder-config/), not on the Organization serializer.

const CONFIG_FIELDS = [
  'is_enabled',
  'default_reminder_enabled',
  'default_notification_enabled',
  'default_offset_hours',
  'default_channel',
  'reminder_sms_text',
  'reminder_email_subject',
  'reminder_email_text',
  'notification_sms_text',
  'notification_email_subject',
  'notification_email_text',
];

export default function ScheduleRemindersTab({ onDirtyChange }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null); // sms_allowed, price, prefix, placeholders, default_texts
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!currentOrganization) return;
    let cancelled = false;
    setForm(null);
    setLoadError(false);
    axios
      .get('/api/scheduling/reminder-config/')
      .then((res) => {
        if (cancelled) return;
        const next = {};
        CONFIG_FIELDS.forEach((field) => {
          next[field] = res.data[field];
        });
        setForm(next);
        setMeta({
          sms_allowed: res.data.sms_allowed,
          sms_price_per_segment: res.data.sms_price_per_segment,
          sms_prefix: res.data.sms_prefix,
          placeholders: res.data.placeholders || [],
          default_texts: res.data.default_texts || {},
        });
        setIsDirty(false);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      await axios.patch('/api/scheduling/reminder-config/', form);
      setIsDirty(false);
      setSnackbar({ open: true, message: t('Organization updated successfully'), severity: 'success' });
    } catch (error) {
      console.error('Failed to update schedule reminder settings:', error);
      setSnackbar({ open: true, message: t('Failed to update organization'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Preview: both messages rendered with the texts on screen, saved or not.
  const loadPreview = async (language) => {
    const res = await axios.post('/api/scheduling/reminder-config/preview/', { ...form, language });
    return res.data;
  };

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadError) {
    return <Alert severity="error">{t('schedule_reminders.load_error')}</Alert>;
  }
  if (!form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const smsPrice = meta?.sms_price_per_segment ? Number(meta.sms_price_per_segment) : null;
  const smsPrefix = meta?.sms_prefix || '';
  const defaults = meta?.default_texts || {};

  const smsCostLine = (bodyText, kind) => {
    const effective = bodyText || defaults[kind]?.sms || '';
    const assembled = effective ? `${smsPrefix}${effective}` : '';
    const segments = assembled ? calculateSmsSegments(assembled).segmentCount : 0;
    if (smsPrice === null || segments === 0) return null;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('schedule_reminders.sms_cost_estimate', { cost: formatCost(smsPrice * segments) })}
      </Typography>
    );
  };

  const messageEditor = (kind) => {
    const smsField = `${kind}_sms_text`;
    const subjectField = `${kind}_email_subject`;
    const bodyField = `${kind}_email_text`;
    const kindKey = kind === 'notification' ? 'notification' : 'reminder';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {t(`schedule_reminders.texts_${kindKey}_title`)}
        </Typography>
        <Box>
          <TextField
            label={t('schedule_reminders.sms_body')}
            multiline
            rows={2}
            size="small"
            fullWidth
            value={form[smsField] || ''}
            onChange={(e) => update({ [smsField]: e.target.value })}
            placeholder={defaults[kindKey]?.sms || ''}
            helperText={t('schedule_reminders.sms_body_help')}
            inputProps={{ 'data-testid': `${kindKey}-sms-text` }}
          />
          <SmsSegmentCounter
            prefix={smsPrefix}
            text={form[smsField] || defaults[kindKey]?.sms || ''}
            defaultMessage=""
          />
          {smsCostLine(form[smsField], kindKey)}
        </Box>
        <TextField
          label={t('schedule_reminders.email_subject')}
          size="small"
          fullWidth
          value={form[subjectField] || ''}
          onChange={(e) => update({ [subjectField]: e.target.value })}
          placeholder={defaults[kindKey]?.email_subject || ''}
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label={t('schedule_reminders.email_body')}
          multiline
          rows={5}
          size="small"
          fullWidth
          value={form[bodyField] || ''}
          onChange={(e) => update({ [bodyField]: e.target.value })}
          placeholder={defaults[kindKey]?.email_body || ''}
          helperText={t('schedule_reminders.email_body_help')}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <FormControlLabel
        control={
          <Switch
            checked={!!form.is_enabled}
            onChange={(e) => update({ is_enabled: e.target.checked })}
            data-testid="schedule-reminders-toggle"
          />
        }
        label={t('schedule_reminders.feature_toggle')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: 2 }}>
        {t('schedule_reminders.feature_toggle_help')}
      </Typography>

      {form.is_enabled && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t('schedule_reminders.defaults_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('schedule_reminders.defaults_help')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.default_reminder_enabled}
                  onChange={(e) => update({ default_reminder_enabled: e.target.checked })}
                  data-testid="default-reminder-toggle"
                />
              }
              label={t('schedule_reminders.default_reminder')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.default_notification_enabled}
                  onChange={(e) => update({ default_notification_enabled: e.target.checked })}
                  data-testid="default-notification-toggle"
                />
              }
              label={t('schedule_reminders.default_notification')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('schedule_reminders.default_offset')}
              type="number"
              size="small"
              value={form.default_offset_hours}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                update({ default_offset_hours: Number.isNaN(val) ? '' : val });
              }}
              inputProps={{ min: 1, max: 336, 'data-testid': 'default-offset-input' }}
              helperText={t('schedule_reminders.default_offset_help')}
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('schedule_reminders.default_channel')}</InputLabel>
              <Select
                value={form.default_channel}
                label={t('schedule_reminders.default_channel')}
                onChange={(e) => update({ default_channel: e.target.value })}
                inputProps={{ 'data-testid': 'default-channel-select' }}
              >
                <MenuItem value="both">{t('schedule_reminders.channel_both')}</MenuItem>
                <MenuItem value="sms">{t('schedule_reminders.channel_sms')}</MenuItem>
                <MenuItem value="email">{t('schedule_reminders.channel_email')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '13px' } }}>
            {t('schedule_reminders.sms_billing_note')}
            {smsPrice !== null && ` ${t('schedule_reminders.sms_price_line', { price: formatCost(smsPrice) })}`}
          </Alert>
          {meta?.sms_allowed === false && (
            <Alert severity="info" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: '13px' } }}>
              {t('schedule_reminders.sms_trial_blocked')}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />
          <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '13px' } }}>
            {t('schedule_reminders.placeholders_help')}{' '}
            <code style={{ fontSize: '12px' }}>{(meta?.placeholders || []).join(' ')}</code>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {messageEditor('reminder')}
            {messageEditor('notification')}
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
        {form.is_enabled && (
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            data-testid="schedule-reminders-preview-button"
          >
            {t('schedule_reminders.preview_button')}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !isDirty}
          data-testid="schedule-reminders-settings-save"
        >
          {saving ? <CircularProgress size={20} /> : <span>{t('Save Changes')}</span>}
        </Button>
      </Box>

      <MessagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={t('schedule_reminders.preview_title')}
        defaultLanguage={currentOrganization?.default_language}
        loadPreview={loadPreview}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
